import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiLogout } from 'react-icons/ci';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { PiPlusSquareThin } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Box, useTheme } from '@mui/joy';
import List from '@mui/joy/List';
import { observer } from 'mobx-react-lite';

import { SiflyLogo } from '../../../general-utilities/SiflyLogo';
import CampaignIcon from '../../../icons/campaigns.svg?react';
import DroneIcon from '../../../icons/drones.svg?react';
import HomeIcon from '../../../icons/home.svg?react';
import PreferencesIcon from '../../../icons/settings.svg?react';
import ShareIcon from '../../../icons/share.svg?react';
import UserIcon from '../../../icons/users.svg?react';
import { CompanyService } from '../../../services/CompanyService';
import organizationStore from '../../../stores/OrganizationStore';
import sessionStore from '../../../stores/SessionStore';
import { IconKey, navItems } from './navItems';
import SideNavHeaderItem from './SideNavHeaderItem';
import SideNavigationItem from './SideNavigationItem';

export const Navigation = observer(() => {
  const theme = useTheme();
  const { t: translate } = useTranslation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const logo = SiflyLogo();

  function getFillColor() {
    if (theme.palette.mode === 'dark') {
      return theme.palette.text.tertiary;
    }
    return theme.palette.text.primary;
  }

  const icons: Record<IconKey, React.ReactElement> = {
    home: <HomeIcon fill={getFillColor()} />,
    drone: <DroneIcon fill={getFillColor()} />,
    droneModel: <DroneIcon fill={getFillColor()} />,
    campaign: <CampaignIcon fill={getFillColor()} />,
    user: <UserIcon fill={getFillColor()} />,
    organization: <PreferencesIcon fill={getFillColor()} />,
    preferences: <UserIcon fill={getFillColor()} />,
    deviceType: <DroneIcon fill={getFillColor()} />,
    device: <DroneIcon fill={getFillColor()} />,
    share: <ShareIcon fill={getFillColor()} />
  };

  useEffect(() => {
    const fetchUserOrganizations = async () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        try {
          const response = await CompanyService.getUserOrganizations();
          const organizationsData = response?.organization_data || [];
          organizationStore.setOrganizations(organizationsData);
          organizationStore.loadSelectedOrg();

          if (!organizationStore.selectedOrg && organizationsData.length > 0) {
            const firstOrgId = organizationsData[0].id;
            organizationStore.setSelectedOrg(firstOrgId);
          }
        } catch {
          toast.error(t('navigation.errors.fetch_organization'));
        }
      }
    };

    fetchUserOrganizations();
  }, [t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getSvgIcon = (icon: IconKey) => icons[icon];

  const getNavItems = () =>
    navItems
      .filter((item) => {
        if (item.type === 'link') {
          // Check for permissions
          if (item.permissions?.length) {
            const userPermissions = sessionStore.user?.permissions || [];
            return item.permissions.some((perm) => userPermissions.some((userPerm) => userPerm.name === perm));
          }
        }
        return true;
      })
      .map((navItem) => {
        const itemTitle = translate(navItem.l10nKey);

        if (navItem.type === 'link') {
          const icon = getSvgIcon(navItem.icon);
          // Check if current path matches this navigation item's path
          // For exact matches or if the current path starts with the nav item path (for nested routes)
          const isActive =
            location.pathname === navItem.to ||
            (navItem.to !== '/dashboard' && location.pathname.startsWith(navItem.to));
          return (
            <SideNavigationItem
              icon={icon}
              isActive={isActive}
              key={navItem.l10nKey}
              palette={theme.palette.mode}
              title={itemTitle}
              toLink={navItem.to}
            />
          );
        }
        if (navItem.type === 'subheader') {
          return <SideNavHeaderItem key={navItem.l10nKey} title={itemTitle} />;
        }
        return null;
      });

  const toggleDropdown = () => {
    setIsDropdownOpen((prevState) => !prevState);
  };

  const handleOrgSelect = (orgId: number) => {
    organizationStore.setSelectedOrg(orgId);
    organizationStore.persistSelectedOrg();
    setIsDropdownOpen(false);
  };

  return (
    <Box
      sx={{
        padding: { xs: '24px', sm: '4px' },
        minHeight: '100%',
        backgroundColor: 'background.surface',
        position: 'relative'
      }}
    >
      <div
        className='flex justify-between items-center py-3 cursor-pointer rounded-lg w-full'
        onClick={toggleDropdown}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); // Prevent scrolling on Space
            toggleDropdown();
          }
        }}
        role='button'
        tabIndex={0}
      >
        <span className='flex items-center justify-center text-base font-medium'>
          <img alt='Organization Logo' className='h-8 w-8 mr-3' src='/images/logos/OrganizationLogo.png' />
          {organizationStore.organizations.find((org) => org.id === organizationStore.selectedOrg)?.name ||
            'Select an Organization'}
        </span>
        <span>{isDropdownOpen ? <IoIosArrowUp size={20} /> : <IoIosArrowDown size={20} />}</span>
      </div>

      {isDropdownOpen && (
        <Box
          className='absolute sm:left-40 w-4/5 sm:w-full py-2 rounded-lg z-10 shadow-md'
          ref={dropdownRef}
          sx={{ backgroundColor: 'background.body' }}
        >
          <Box
            sx={{
              borderBottomWidth: '1px',
              borderColor: 'neutral.200',
              maxHeight: '10rem',
              overflow: 'scroll'
            }}
          >
            {organizationStore.organizations.map((org) => (
              <button
                className='cursor-pointer flex items-center w-full py-2 px-2'
                key={org.id}
                onClick={() => handleOrgSelect(org.id)}
                style={{
                  background: organizationStore.selectedOrg === org.id ? theme.palette.neutral[700] : '',
                  color: organizationStore.selectedOrg === org.id ? theme.palette.neutral[100] : ''
                }}
                type='button'
              >
                <img alt='Organization Logo' className='h-8 w-8 mr-3' src='images/logos/OrganizationLogo.png' />
                <span className='text-base font-normal'>{org.name}</span>
              </button>
            ))}
          </Box>

          <button
            className='cursor-pointer flex items-center w-full py-2 px-2'
            onClick={() => navigate('/create-organization')}
            type='button'
          >
            <PiPlusSquareThin className='mr-3' size={20} />
            <span className='text-base font-normal'>Create new Organization</span>
          </button>

          <button
            className='cursor-pointer flex items-center w-full py-2 px-2'
            onClick={() => navigate('/logout')}
            type='button'
          >
            <CiLogout className='transform -scale-x-100 mr-3' size={20} />
            <span className='text-base font-normal'>Log out</span>
          </button>
        </Box>
      )}

      <List
        size='sm'
        sx={{
          '--ListItem-radius': '8px',
          '--List-gap': '8px',
          '--listItemDecorator-size': '24px'
        }}
      >
        {getNavItems()}
      </List>
      <Box sx={{ width: 100, height: 40, position: 'absolute', bottom: '0px' }}>
        <img alt='Sifly Logo' src={logo} />
      </Box>
    </Box>
  );
});
